import React, { useState, useEffect, useCallback } from 'react';
import { 
  Shield,
  Lock,
  Smartphone,
  Key,
  LogOut,
  Monitor,
  Clock,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
  Trash2,
  History,
  Loader2,
  QrCode
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { useAuth } from '../lib/AuthContext';
import api from '../lib/api';

const SessionCard = ({ session, onRevoke, isCurrent }) => {
  const [revoking, setRevoking] = useState(false);
  
  const handleRevoke = async () => {
    setRevoking(true);
    await onRevoke(session.id);
    setRevoking(false);
  };
  
  // Parse user agent for display
  const parseUserAgent = (ua) => {
    if (!ua) return { browser: 'Unknown', os: 'Unknown' };
    
    let browser = 'Unknown';
    let os = 'Unknown';
    
    if (ua.includes('Chrome')) browser = 'Chrome';
    else if (ua.includes('Firefox')) browser = 'Firefox';
    else if (ua.includes('Safari')) browser = 'Safari';
    else if (ua.includes('Edge')) browser = 'Edge';
    
    if (ua.includes('Windows')) os = 'Windows';
    else if (ua.includes('Mac')) os = 'macOS';
    else if (ua.includes('Linux')) os = 'Linux';
    else if (ua.includes('Android')) os = 'Android';
    else if (ua.includes('iPhone')) os = 'iOS';
    
    return { browser, os };
  };
  
  const { browser, os } = parseUserAgent(session.user_agent);
  
  return (
    <div className={`p-4 rounded-lg border ${isCurrent ? 'border-primary bg-primary/5' : 'border-border'}`}>
      <div className="flex justify-between items-start">
        <div className="flex gap-3">
          <div className="p-2 bg-muted rounded-lg">
            <Monitor className="w-5 h-5" />
          </div>
          <div>
            <div className="font-medium flex items-center gap-2">
              {browser} em {os}
              {isCurrent && <Badge variant="outline" className="text-xs">Sessão atual</Badge>}
            </div>
            <div className="text-sm text-muted-foreground">
              IP: {session.ip_address || 'Unknown'}
            </div>
            <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <Clock className="w-3 h-3" />
              Último acesso: {new Date(session.last_activity).toLocaleString('pt-BR')}
            </div>
          </div>
        </div>
        
        {!isCurrent && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleRevoke}
            disabled={revoking}
          >
            {revoking ? <Loader2 className="w-4 h-4 animate-spin" /> : <LogOut className="w-4 h-4" />}
          </Button>
        )}
      </div>
    </div>
  );
};

const AuditLogRow = ({ log }) => {
  const actionIcons = {
    login: <CheckCircle className="w-4 h-4 text-green-400" />,
    logout: <LogOut className="w-4 h-4 text-blue-400" />,
    login_failed: <XCircle className="w-4 h-4 text-red-400" />,
    password_change: <Key className="w-4 h-4 text-yellow-400" />,
    '2fa_enabled': <Shield className="w-4 h-4 text-green-400" />,
    '2fa_disabled': <Shield className="w-4 h-4 text-red-400" />,
    session_revoked: <LogOut className="w-4 h-4 text-orange-400" />,
    suspicious_activity: <AlertTriangle className="w-4 h-4 text-red-400" />
  };
  
  const actionLabels = {
    login: 'Login realizado',
    logout: 'Logout',
    login_failed: 'Login falhou',
    password_change: 'Senha alterada',
    '2fa_enabled': '2FA ativado',
    '2fa_disabled': '2FA desativado',
    session_revoked: 'Sessão encerrada',
    suspicious_activity: 'Atividade suspeita'
  };
  
  return (
    <div className="flex items-center gap-3 py-3 border-b border-border/50 last:border-0">
      <div className="p-2 bg-muted rounded-lg">
        {actionIcons[log.action] || <History className="w-4 h-4" />}
      </div>
      <div className="flex-1">
        <p className="font-medium text-sm">{actionLabels[log.action] || log.action}</p>
        <p className="text-xs text-muted-foreground">
          {log.ip_address && `IP: ${log.ip_address} • `}
          {new Date(log.timestamp).toLocaleString('pt-BR')}
        </p>
      </div>
      <Badge 
        variant={log.severity === 'warning' ? 'destructive' : 'secondary'}
        className="text-xs"
      >
        {log.severity}
      </Badge>
    </div>
  );
};

export default function SecuritySettings() {
  const { user, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [auditLogs, setAuditLogs] = useState([]);
  const [twoFactorStatus, setTwoFactorStatus] = useState({ enabled: false, has_backup_codes: false });
  
  // 2FA setup state
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verifyCode, setVerifyCode] = useState('');
  const [setupStep, setSetupStep] = useState('init'); // init, qr, verify
  const [setupPassword, setSetupPassword] = useState('');
  
  // Disable 2FA state
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  const [disablePassword, setDisablePassword] = useState('');
  const [disableCode, setDisableCode] = useState('');
  
  // Change password state
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current: '',
    new: '',
    confirm: ''
  });
  
  const [actionLoading, setActionLoading] = useState(false);
  
  const fetchData = useCallback(async () => {
    try {
      const [sessionsRes, logsRes, statusRes] = await Promise.all([
        api.get('/auth/sessions'),
        api.get('/auth/audit-logs?limit=20'),
        api.get('/auth/2fa/status')
      ]);
      
      setSessions(sessionsRes.data.sessions || []);
      setAuditLogs(logsRes.data.logs || []);
      setTwoFactorStatus(statusRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados de segurança');
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    fetchData();
  }, [fetchData]);
  
  const handleRevokeSession = async (sessionId) => {
    try {
      await api.delete(`/auth/sessions/${sessionId}`);
      toast.success('Sessão encerrada');
      fetchData();
    } catch (error) {
      toast.error('Erro ao encerrar sessão');
    }
  };
  
  const handleRevokeAllSessions = async () => {
    try {
      await api.post('/auth/logout-all');
      toast.success('Todas as outras sessões foram encerradas');
      fetchData();
    } catch (error) {
      toast.error('Erro ao encerrar sessões');
    }
  };
  
  const handleEnable2FA = async () => {
    if (!setupPassword) {
      toast.error('Digite sua senha');
      return;
    }
    
    setActionLoading(true);
    try {
      const res = await api.post('/auth/2fa/enable', { password: setupPassword });
      setSetupData(res.data);
      setSetupStep('qr');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao iniciar 2FA');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleVerify2FA = async () => {
    if (!verifyCode || verifyCode.length !== 6) {
      toast.error('Digite o código de 6 dígitos');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post('/auth/2fa/verify', { code: verifyCode });
      toast.success('2FA ativado com sucesso!');
      setShow2FASetup(false);
      setSetupStep('init');
      setSetupData(null);
      setVerifyCode('');
      setSetupPassword('');
      fetchData();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleDisable2FA = async () => {
    if (!disablePassword || !disableCode) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post('/auth/2fa/disable', { 
        password: disablePassword, 
        code: disableCode 
      });
      toast.success('2FA desativado');
      setShowDisable2FA(false);
      setDisablePassword('');
      setDisableCode('');
      fetchData();
      if (refreshUser) refreshUser();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao desativar 2FA');
    } finally {
      setActionLoading(false);
    }
  };
  
  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.new || !passwordForm.confirm) {
      toast.error('Preencha todos os campos');
      return;
    }
    
    if (passwordForm.new !== passwordForm.confirm) {
      toast.error('As senhas não coincidem');
      return;
    }
    
    if (passwordForm.new.length < 8) {
      toast.error('A nova senha deve ter pelo menos 8 caracteres');
      return;
    }
    
    setActionLoading(true);
    try {
      await api.post('/auth/change-password', {
        current_password: passwordForm.current,
        new_password: passwordForm.new
      });
      toast.success('Senha alterada com sucesso!');
      setShowChangePassword(false);
      setPasswordForm({ current: '', new: '', confirm: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao alterar senha');
    } finally {
      setActionLoading(false);
    }
  };
  
  const copyBackupCodes = () => {
    if (setupData?.backup_codes) {
      navigator.clipboard.writeText(setupData.backup_codes.join('\n'));
      toast.success('Códigos copiados!');
    }
  };
  
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }
  
  return (
    <div className="p-6 space-y-6" data-testid="security-settings">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <Shield className="w-7 h-7 text-primary" />
          Segurança
        </h1>
        <p className="text-muted-foreground">
          Gerencie a segurança da sua conta
        </p>
      </div>
      
      <Tabs defaultValue="overview" className="space-y-6">
        <TabsList>
          <TabsTrigger value="overview">Visão Geral</TabsTrigger>
          <TabsTrigger value="sessions">Sessões</TabsTrigger>
          <TabsTrigger value="logs">Logs de Atividade</TabsTrigger>
        </TabsList>
        
        <TabsContent value="overview" className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Two-Factor Authentication */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Smartphone className="w-5 h-5" />
                  Autenticação em Duas Etapas (2FA)
                </CardTitle>
                <CardDescription>
                  Adicione uma camada extra de segurança à sua conta
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    {twoFactorStatus.enabled ? (
                      <>
                        <CheckCircle className="w-5 h-5 text-green-400" />
                        <span className="text-green-400 font-medium">Ativado</span>
                      </>
                    ) : (
                      <>
                        <XCircle className="w-5 h-5 text-muted-foreground" />
                        <span className="text-muted-foreground">Desativado</span>
                      </>
                    )}
                  </div>
                </div>
                
                {twoFactorStatus.enabled ? (
                  <Button 
                    variant="destructive" 
                    onClick={() => setShowDisable2FA(true)}
                    className="w-full"
                  >
                    Desativar 2FA
                  </Button>
                ) : (
                  <Button 
                    onClick={() => setShow2FASetup(true)}
                    className="w-full"
                  >
                    <Shield className="w-4 h-4 mr-2" />
                    Ativar 2FA
                  </Button>
                )}
              </CardContent>
            </Card>
            
            {/* Password */}
            <Card className="glass-card">
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Senha
                </CardTitle>
                <CardDescription>
                  Altere sua senha regularmente para maior segurança
                </CardDescription>
              </CardHeader>
              <CardContent>
                <Alert className="mb-4">
                  <AlertTriangle className="w-4 h-4" />
                  <AlertDescription>
                    Use uma senha forte com pelo menos 8 caracteres, incluindo letras maiúsculas, minúsculas, números e símbolos.
                  </AlertDescription>
                </Alert>
                
                <Button 
                  variant="outline" 
                  onClick={() => setShowChangePassword(true)}
                  className="w-full"
                >
                  <Lock className="w-4 h-4 mr-2" />
                  Alterar Senha
                </Button>
              </CardContent>
            </Card>
          </div>
          
          {/* Active Sessions Summary */}
          <Card className="glass-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Monitor className="w-5 h-5" />
                    Sessões Ativas
                  </CardTitle>
                  <CardDescription>
                    {sessions.length} dispositivo(s) conectado(s)
                  </CardDescription>
                </div>
                <Button variant="outline" onClick={handleRevokeAllSessions}>
                  <LogOut className="w-4 h-4 mr-2" />
                  Encerrar Outras Sessões
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {sessions.slice(0, 3).map((session, index) => (
                <SessionCard 
                  key={session.id} 
                  session={session} 
                  onRevoke={handleRevokeSession}
                  isCurrent={index === 0}
                />
              ))}
              {sessions.length > 3 && (
                <p className="text-sm text-muted-foreground text-center">
                  + {sessions.length - 3} outras sessões
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="sessions">
          <Card className="glass-card">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Todas as Sessões</CardTitle>
                <Button variant="outline" size="sm" onClick={fetchData}>
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Atualizar
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {sessions.map((session, index) => (
                <SessionCard 
                  key={session.id} 
                  session={session} 
                  onRevoke={handleRevokeSession}
                  isCurrent={index === 0}
                />
              ))}
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="logs">
          <Card className="glass-card">
            <CardHeader>
              <CardTitle>Logs de Atividade</CardTitle>
              <CardDescription>
                Histórico de ações de segurança na sua conta
              </CardDescription>
            </CardHeader>
            <CardContent>
              {auditLogs.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <History className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>Nenhum log de atividade</p>
                </div>
              ) : (
                auditLogs.map(log => (
                  <AuditLogRow key={log.id} log={log} />
                ))
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      {/* 2FA Setup Dialog */}
      <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Shield className="w-5 h-5" />
              Configurar 2FA
            </DialogTitle>
          </DialogHeader>
          
          {setupStep === 'init' && (
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Digite sua senha para iniciar a configuração do 2FA.
              </p>
              <div>
                <Label>Senha</Label>
                <Input
                  type="password"
                  value={setupPassword}
                  onChange={e => setSetupPassword(e.target.value)}
                  placeholder="Digite sua senha"
                />
              </div>
              <Button onClick={handleEnable2FA} disabled={actionLoading} className="w-full">
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Continuar
              </Button>
            </div>
          )}
          
          {setupStep === 'qr' && setupData && (
            <div className="space-y-4 py-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  Escaneie o QR code com seu app autenticador (Google Authenticator, Authy, etc.)
                </p>
                <img 
                  src={setupData.qr_code} 
                  alt="QR Code 2FA" 
                  className="mx-auto rounded-lg"
                  style={{ maxWidth: '200px' }}
                />
              </div>
              
              <div className="bg-muted p-3 rounded-lg">
                <p className="text-xs text-muted-foreground mb-2">Chave manual:</p>
                <code className="text-sm break-all">{setupData.secret}</code>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 p-3 rounded-lg">
                <p className="text-xs text-yellow-400 font-medium mb-2">
                  Códigos de backup (guarde em local seguro):
                </p>
                <div className="grid grid-cols-2 gap-1">
                  {setupData.backup_codes?.map((code, i) => (
                    <code key={`backup-${code}`} className="text-xs">{code}</code>
                  ))}
                </div>
                <Button size="sm" variant="ghost" onClick={copyBackupCodes} className="mt-2 w-full">
                  <Copy className="w-4 h-4 mr-2" />
                  Copiar Códigos
                </Button>
              </div>
              
              <div>
                <Label>Digite o código do app</Label>
                <Input
                  type="text"
                  value={verifyCode}
                  onChange={e => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="000000"
                  maxLength={6}
                  className="text-center text-2xl tracking-widest"
                />
              </div>
              
              <Button onClick={handleVerify2FA} disabled={actionLoading || verifyCode.length !== 6} className="w-full">
                {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                Verificar e Ativar
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* Disable 2FA Dialog */}
      <Dialog open={showDisable2FA} onOpenChange={setShowDisable2FA}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Desativar 2FA</DialogTitle>
            <DialogDescription>
              Isso tornará sua conta menos segura. Você precisará da sua senha e um código do autenticador.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Senha</Label>
              <Input
                type="password"
                value={disablePassword}
                onChange={e => setDisablePassword(e.target.value)}
                placeholder="Digite sua senha"
              />
            </div>
            <div>
              <Label>Código 2FA</Label>
              <Input
                type="text"
                value={disableCode}
                onChange={e => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDisable2FA(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDisable2FA} disabled={actionLoading}>
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Desativar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Change Password Dialog */}
      <Dialog open={showChangePassword} onOpenChange={setShowChangePassword}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Alterar Senha</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div>
              <Label>Senha Atual</Label>
              <Input
                type="password"
                value={passwordForm.current}
                onChange={e => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
              />
            </div>
            <div>
              <Label>Nova Senha</Label>
              <Input
                type="password"
                value={passwordForm.new}
                onChange={e => setPasswordForm(prev => ({ ...prev, new: e.target.value }))}
              />
            </div>
            <div>
              <Label>Confirmar Nova Senha</Label>
              <Input
                type="password"
                value={passwordForm.confirm}
                onChange={e => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowChangePassword(false)}>
              Cancelar
            </Button>
            <Button onClick={handleChangePassword} disabled={actionLoading}>
              {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Alterar Senha
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
